import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import history from '@history';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import clsx from 'clsx';
import { Chip } from '@mui/material';

const stickyRightStyled = 'md:sticky md:right-0 md:border-l-1';
const stickyLeftStyled = 'md:sticky md:left-0 md:border-r-1';

/**
 * DashboardTable
 *
 * Componente de tabla para dashboards, con encabezados, filas y enlace opcional.
 *
 * @component
 * @param {string} [className] - Clases CSS adicionales para el contenedor
 * @param {object} [style] - Estilos adicionales para el contenedor
 * @param {number|string} [height] - Altura de la tabla
 * @param {Array<object>} [headers=[]] - Definición de encabezados de la tabla
 * @param {Array<object>} [data=[]] - Datos a mostrar en la tabla
 * @param {{ label: string, color?: string, variant?: string, path?: string }} [link] - Objeto de enlace para mostrar un chip de acción
 * @param {string} [label='Tabla dashboard'] - Título de la tabla
 * @param {boolean} [small=false] - Si la tabla es de tamaño pequeño
 * @returns {JSX.Element}
 */
export const DashboardTable = ({
  className,
  style,
  height,
  headers = [],
  data = [],
  link,
  label = 'Tabla dashboard',
  small = false,
}) => {
  const theme = useTheme();

  const handleRedirect = (path) => {
    if (path === undefined) return;
    history.push(path);
  };

  return (
    <Paper
      sx={style}
      className={clsx(
        'flex flex-col shadow rounded-2xl overflow-hidden align-center justify-start px-12 pb-12 pt-14 min-w-[320px]',
        className,
        height && `h-[${height}px]`
      )}
    >
      <div className="flex flex-row w-full items-center justify-between px-12">
        <Typography className="text-lg font-medium tracking-tight leading-6 truncate">
          {label}
        </Typography>
        {link !== undefined && (
          <Chip
            size="small"
            color={link?.color}
            variant={link.variant || 'filled'}
            disabled={link.path === undefined}
            onClick={() => handleRedirect(link?.path)}
            label={link.label || 'Ver más'}
          />
        )}
      </div>
      {headers.length > 0 ? (
        <TableContainer>
          <Table aria-label="simple table" stickyHeader size={small ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                {headers.map(
                  (
                    { title, width, minWidth, maxWidth, fixedRight, fixedLeft, align = 'center' },
                    index
                  ) => (
                    <TableCell
                      key={`${title}_${index}`}
                      style={{
                        width,
                        minWidth,
                        maxWidth,
                        backgroundColor: theme.palette.background.paper,
                        borderBottomWidth: '1px',
                        borderBottomColor: '#DDD',
                      }}
                      align={align}
                      className={`${
                        fixedRight && !fixedLeft
                          ? `${stickyRightStyled} md:border-l-neutral-700 md:shadow-md`
                          : ''
                      } ${
                        !fixedLeft && fixedLeft
                          ? `${stickyLeftStyled} md:border-r-neutral-700 md:shadow-md`
                          : ''
                      }`}
                    >
                      {title}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            {data !== undefined && data.length > 0 && (
              <TableBody>
                {data.map((item, indexRow) => (
                  <TableRow key={`indx_tbl_${indexRow}`}>
                    {headers.map((option, indexOption) => (
                      <TableCell
                        key={`indx_opt_${indexOption}`}
                        align="center"
                        className={`${
                          option?.fixed && option.fixed === 'right'
                            ? `${stickyRightStyled} md:border-l-neutral-400 md:shadow-md`
                            : ''
                        } ${
                          option?.fixed && option.fixed === 'left'
                            ? `${stickyLeftStyled} md:border-r-neutral-400 md:shadow-md`
                            : ''
                        } `}
                      >
                        {option.assignment !== undefined ? item[option.assignment] : 'N/D'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {(data.length === 0 || data === undefined) && (
            <div className="mt-16 mb-24 w-full flex justify-center">
              <Typography variant="body2">No hay datos que mostrar</Typography>
            </div>
          )}
        </TableContainer>
      ) : (
        <div className="flex flex-col justify-center items-center h-full w-full">
          <Typography variant="body2">Tabla sin esquema configurado</Typography>
        </div>
      )}
    </Paper>
  );
};
